/* @flow */

import type { NETGEM_API_PARENTAL_CONTROL } from '../../../../libs/netgemLibrary/v8/types/ParentalControl';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { Dispatch } from '../../../types/types';
import type { RequestResponseMethodDefinitionType } from '../emitter';

import Localizer from '../../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../../helpers/messenger';
import ParentalControlManager from '../../../../helpers/parentalControl/parentalControlManager';
import { CustomNetworkError } from '../../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { PersonalDataKey } from '../../../../libs/netgemLibrary/personalData/constants/keys';
import { HttpStatus } from '../../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { sendPersonalDataGetRequest } from './get';
import { sendPersonalDataPostRequest } from './post';

const getParentalControl: (signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (signal) =>
  (dispatch: Dispatch): Promise<?string> => {
    const initialETag = ParentalControlManager.eTag;

    return dispatch(sendPersonalDataGetRequest(PersonalDataKey.ParentalControl, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag } = data;
        const result = JSON.parse(data.result as string);
        const parentalControl: NETGEM_API_PARENTAL_CONTROL = result as NETGEM_API_PARENTAL_CONTROL;

        ParentalControlManager.setAllRestrictions(parentalControl, true, eTag);
        return eTag;
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (status === HttpStatus.NotFound) {
          // Not found means parental control does not exist yet
          ParentalControlManager.resetETag();
          ParentalControlManager.unlock(false);
          return null;
        } else if (status === HttpStatus.NotModified) {
          return initialETag;
        }

        throw error;
      });
  };

const sendParentalControlUpdateRequest: (
  parentalControl: NETGEM_API_PARENTAL_CONTROL,
  initialEtag: string | null,
  signal?: AbortSignal,
  isSecondAttempt?: boolean,
) => RequestResponseMethodDefinitionType =
  (parentalControl, initialETag, signal, isSecondAttempt) =>
  (dispatch: Dispatch): Promise<void> =>
    dispatch(sendPersonalDataPostRequest(PersonalDataKey.ParentalControl, parentalControl, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag } = data;
        ParentalControlManager.setAllRestrictions(parentalControl, false, eTag);
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (!isSecondAttempt && (status === HttpStatus.Conflict || status === HttpStatus.PreconditionFailed)) {
          /*
           * Most probable cause: another device paired with the same account already updated the parental control
           * So we send a GET request to retrieve the latest ETag, but we only try once to avoid infinite loop between multiple devices
           */
          ParentalControlManager.resetETag();

          return dispatch(getParentalControl(signal))
            .then((newETag) => dispatch(sendParentalControlUpdateRequest(parentalControl, newETag, signal, true)))
            .catch((error) => {
              // GET request failed: nothing to update
              Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('settings.parental_control.update_error'));
              throw error;
            });
        }

        throw error;
      });

export { getParentalControl, sendParentalControlUpdateRequest };
